import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import CheckboxField from 'components/Fields/Checkbox/Checkbox';
import ReportSection from 'components/InvestmentAnalyser/ReportSections';

const useStyles = makeStyles({
  checkboxContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    fontSize: '0.8rem',
    lineHeight: '1rem',
    color: '#747474'
  },
  itemRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  checkboxLabel: {
    fontSize: '10px'
  }
});

interface ReportOptionsProps {
  sections: any;
  setSelectedSections: Function;
  recommendedPolicies?: any;
  existingPolicies?: any;
  forceShowUnderlyingFunds?: boolean;
}

const ReportOptions = ({
  sections,
  setSelectedSections,
  recommendedPolicies,
  existingPolicies,
  forceShowUnderlyingFunds = false
}: ReportOptionsProps) => {
  const hasModelPortfolio = (policies: any) => {
    return (
      policies &&
      policies.some((policy: any) =>
        policy.fund_allocations.some(
          (fund: any) => fund.model_portfolio !== null
        )
      )
    );
  };

  const isModelPortfolioInPolicies =
    hasModelPortfolio(recommendedPolicies) ||
    hasModelPortfolio(existingPolicies);

  const [isAllSelected, setIsAllSelected] = useState(true);

  const defaultReportSections = sections.flatMap((section: any) =>
    section.items
      .filter(
        (name: any) =>
          !(
            recommendedPolicies &&
            recommendedPolicies.length === 1 &&
            name === 'Consolidated portfolio'
          )
      )
      .filter((name: any) => {
        if (name === 'Show underlying funds') {
          return forceShowUnderlyingFunds ? true : isModelPortfolioInPolicies;
        }

        return true;
      })
      .map((name: any) => ({
        name,
        heading: section.title || '', // Ensure heading matches section title
        isSelected:
          name === 'Show underlying funds' && forceShowUnderlyingFunds
            ? false
            : true
      }))
  );

  const [reportSections, setReportSections] = useState(defaultReportSections);

  useEffect(() => {
    setSelectedSections(defaultReportSections);
  }, []);

  const classes = useStyles();

  const selectItem = (index: number) => {
    const items = reportSections as any[];
    const isSelected = items[index].isSelected;
    items[index].isSelected = !isSelected;
    setReportSections([...items] as any);
    setSelectedSections([...items] as any);
  };

  const groupedReportSections = reportSections.reduce(
    (groupedSections: any, reportSection: any, index: number) => {
      // Proceed with grouping for other sections
      const existingGroup = groupedSections.find(
        (group: any) => group[0].data.heading === reportSection.heading
      );
      if (existingGroup) {
        existingGroup.push({ data: reportSection, index });
      } else {
        groupedSections.push([{ data: reportSection, index }]);
      }
      return groupedSections;
    },
    []
  );

  const updateSelections = (value: boolean) => {
    if (reportSections.length) {
      const mappedItems = reportSections.map((item: any) => {
        item.isSelected = value;
        return item;
      });
      setReportSections(mappedItems);
      setSelectedSections(mappedItems);
    }
  };

  return (
    <>
      <div className={classes.checkboxContainer}>
        <div style={{ marginRight: '1rem' }}>Select All</div>
        <CheckboxField
          meta={{
            touched: false,
            invalid: false,
            error: false
          }}
          fullWidth={false}
          input={{
            value: isAllSelected,
            onChange: () => {
              setIsAllSelected(!isAllSelected);
              updateSelections(!isAllSelected);
            },
            checked: isAllSelected,
            className: classes.checkboxLabel
          }}
        />
      </div>
      <div>
        {groupedReportSections.map((group: any, groupIndex: number) => (
          <div key={groupIndex}>
            <div
              style={{
                marginTop: '0.5rem',
                fontWeight: 600
              }}
            >
              {group[0].data.heading}
            </div>
            {/* Render the heading only once */}
            {group.map(({ data, index }: any) => (
              <div key={index}>
                <div className={classes.itemRow}>
                  <ReportSection
                    data={data}
                    selectItem={selectItem}
                    index={index}
                  />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
};

export default ReportOptions;
